import React, { useEffect, useCallback } from 'react';
import { useBranch } from 'baobab-react/hooks';
import { forEach, get, reject } from 'lodash';
import { Redirect } from 'react-router';
import { Grid, Col, Row } from '@zendeskgarden/react-grid';
import { MD } from '@zendeskgarden/react-typography';
import Flex from 'styled-flex-component';

import { HelmetWrapper } from 'utility/DefaultMeta';
import Spinner from 'components/Spinner/Spinner';
import Alert from 'theme/Alert';
import PageViewWrapper from 'higherOrder/PageViewWrapper';
import {
  getRelatedListingsToProducts,
  getProductDetails,
  addProductToWishlist,
} from 'state/products/actions';
import { variables } from 'theme/variables';
import { matchShape, historyShape } from 'propTypesObjects';
import BottomBanner from 'routes/ProductsPage/components/BottomBanner';
import { getLandingCount } from 'state/landing/actions';
import { Button } from 'theme/Button';
import ProductsHero from './components/ProductsHero/ProductsHero';
import ProductsPipeFacts from './components/ProductsPipeFacts/ProductsPipeFacts';
import {
  ProductsPage,
  ProductsSection,
  ProductsSectionInner,
  ProductsSectionHeader,
} from './Products.styles';
import RelatedListingsMiniTable from './components/RelatedListings/RelatedListingsMiniTable';

const { spacing_md: spacingMd } = variables;

function getPageMeta(productData) {
  if (!productData) {
    return null;
  }
  const { name: title, description } = productData;
  const meta = [];
  const properties = { title, description };

  forEach(properties, (v, k) => {
    if (v) {
      meta.push({ property: `og:${k}`, content: v });
      meta.push({ name: `twitter:${k}`, content: v });
    }
  });
  meta.push({ name: 'description', content: description });
  meta.push({ name: 'robots', content: 'all' });

  const json = {
    '@context': 'http://schema.org/',
    '@type': 'product',
    name: title,
    description,
  };

  const script = [
    { type: 'application/ld+json', innerHTML: JSON.stringify(json, null, 2) },
  ];

  const link = [];
  return { title, meta, link, script };
}

function Products({ match, history }) {
  const productId = match?.params?.id;
  const {
    similarListings,
    user,
    productDetails = {},
    landingCount,
  } = useBranch({
    productDetails: ['products', 'productDetails', productId],
    user: ['authentication', 'session'],
    similarListings: ['products', 'similarListings', productId || ''],
    landingCount: ['landing', 'landing', 'result'],
  });

  const metadata = getPageMeta(productDetails.result);
  const productSlug = get(productDetails, 'result.slug');
  const routeSlug = match?.params?.slug;
  const slugsMatch = !!productSlug && routeSlug === productSlug;
  const otherListings = get(similarListings, 'result.docs');
  const productCriteria = get(productDetails, 'result.criteria');
  const productResultId = get(productDetails.result, 'id');

  useEffect(() => {
    getProductDetails(productId);
  }, [productId]);

  useEffect(() => {
    getLandingCount();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (productCriteria) {
      getRelatedListingsToProducts({
        criteria: productCriteria,
        id: productResultId,
        filters: { status: ['active'] },
      });
    }
  }, [productCriteria, productResultId]);

  const onAddToWishlist = useCallback(async () => {
    if (get(user, 'user_id')) {
      const additionToWishlist = await addProductToWishlist({
        productId,
        user,
        product: productDetails?.result,
      });
      return additionToWishlist;
    }
    return Promise.resolve();
  }, [productDetails.result, productId, user]);

  return (
    <ProductsPage>
      {productSlug && !slugsMatch ? (
        <Redirect
          from={`/p/${routeSlug}/${productId}`}
          to={`/p/${productSlug}/${productId}`}
        />
      ) : null}

      {productDetails.result ? <HelmetWrapper metadata={metadata} /> : null}

      {productDetails.loading && !get(productDetails, 'result') ? (
        <Spinner size="20px" />
      ) : null}

      {productDetails.error ? (
        <ProductsSection>
          <ProductsSectionInner>
            <Alert
              style={{ marginBottom: spacingMd }}
              small
              title={productDetails.error}
              type="error"
            />
          </ProductsSectionInner>
        </ProductsSection>
      ) : null}
      {productDetails.result ? (
        <div>
          <ProductsSection>
            <ProductsSectionInner first={!productDetails.error}>
              <ProductsHero
                userId={get(user, 'user_id')}
                productDetails={productDetails.result}
                onAddToWishlist={onAddToWishlist}
              />
            </ProductsSectionInner>
          </ProductsSection>
          <ProductsSection>
            <ProductsSectionInner style={{ marginTop: '-150px' }}>
              <ProductsPipeFacts
                active={get(productDetails.result, 'brand_name', 'CRA')}
                productDetails={productDetails.result}
                isProductPage
              />
            </ProductsSectionInner>
          </ProductsSection>
          {get(otherListings, 'length') ? (
            <ProductsSection style={{ marginBottom: 0 }}>
              <ProductsSectionInner style={{ marginTop: '-100px' }}>
                <Grid>
                  <Row>
                    <Col>
                      <ProductsSectionHeader
                        tag="h2"
                        style={{ fontSize: '26px', marginBottom: '20px' }}
                        paddingBottomMd
                        scaled
                        bold
                      >
                        {`Recommended Listings similar to ${productDetails?.result?.name}`}
                      </ProductsSectionHeader>
                    </Col>
                  </Row>
                </Grid>
                <RelatedListingsMiniTable
                  data={reject(otherListings, { id: Number(productResultId) })}
                />
              </ProductsSectionInner>
            </ProductsSection>
          ) : null}
        </div>
      ) : (
        <Flex justifyCenter alignCenter column style={{ height: '500px' }}>
          <MD style={{ marginBottom: '20px' }}>
            This Product page is not available.
          </MD>
          <Button
            primary
            onClick={() => {
              history.goBack();
            }}
          >
            Go Back
          </Button>
        </Flex>
      )}
      <BottomBanner counts={landingCount} />
    </ProductsPage>
  );
}

Products.propTypes = {
  match: matchShape,
  history: historyShape,
};

export default PageViewWrapper(Products, { pageName: 'Products' });
